import { Fragment, useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react'
import { router } from '@inertiajs/react'
import { LoanBadge, loanLabel } from '@/lib/loanIdentifiers'
import type { LoanProps, PaymentRecord, BorrowerWithLoans } from '@/types/loan'

interface Props {
  open: boolean
  onClose: () => void
  /** Full loan data when opened from a loan detail page */
  loan?: LoanProps
  /** Borrowers with their active loans, for the two-step selector */
  borrowersWithLoans?: BorrowerWithLoans[]
  /** If provided, modal is in edit mode */
  payment?: PaymentRecord
  /** Loan ID override for edit mode when full loan data isn't available */
  loanId?: number
}

type Step = 'select-borrower' | 'select-loan' | 'form'

export default function RecordPaymentModal({ open, onClose, loan, borrowersWithLoans, payment, loanId: loanIdProp }: Props) {
  const isEditMode = !!payment

  // Selection state
  const [selectedBorrowerId, setSelectedBorrowerId] = useState<number | null>(null)
  const [selectedLoan, setSelectedLoan] = useState<LoanProps | null>(null)
  const [originalLoanId, setOriginalLoanId] = useState<number | null>(null)
  const [step, setStep] = useState<Step>('form')

  // Form state
  const [amount, setAmount] = useState('')
  const [date, setDate] = useState(new Date().toISOString().split('T')[0])
  const [note, setNote] = useState('')
  const [lateFee, setLateFee] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // Error & success state
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [generalError, setGeneralError] = useState('')
  const [showSuccess, setShowSuccess] = useState(false)

  // Borrower search
  const [borrowerSearch, setBorrowerSearch] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)

  // Resolve the active loan for form display
  const activeLoan = selectedLoan ?? loan ?? null
  const expected = activeLoan?.expected_next_payment ?? null

  // Filtered borrowers for search
  const filteredBorrowers = useMemo(() => {
    if (!borrowersWithLoans) return []
    if (!borrowerSearch) return borrowersWithLoans
    const q = borrowerSearch.toLowerCase()
    return borrowersWithLoans.filter((b) => b.name.toLowerCase().includes(q))
  }, [borrowersWithLoans, borrowerSearch])

  // Get selected borrower's active loans
  const selectedBorrowerLoans = useMemo(() => {
    if (!borrowersWithLoans || !selectedBorrowerId) return []
    return borrowersWithLoans.find((b) => b.id === selectedBorrowerId)?.active_loans ?? []
  }, [borrowersWithLoans, selectedBorrowerId])

  // Pre-fill amount when loan changes (create mode only)
  useEffect(() => {
    if (expected && !isEditMode) {
      setAmount(String(expected.amount))
    }
  }, [expected, isEditMode])

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setSubmitting(false)
      setFormErrors({})
      setGeneralError('')
      setShowSuccess(false)
      setBorrowerSearch('')

      if (isEditMode && payment) {
        // Edit mode: pre-fill form, determine loan context
        setAmount(String(payment.amount))
        setDate(payment.date)
        setNote(payment.note || '')
        setLateFee(payment.late_fee > 0 ? String(payment.late_fee) : '')
        setOriginalLoanId(loan?.id ?? loanIdProp ?? null)

        if (loan) {
          setSelectedLoan(loan)
          setSelectedBorrowerId(loan.borrower_id)
          setStep('form')
        } else if (loanIdProp && borrowersWithLoans) {
          // Find the loan from borrowersWithLoans
          const found = findLoanById(borrowersWithLoans, loanIdProp)
          if (found) {
            setSelectedBorrowerId(found.borrowerId)
            setSelectedLoan(found.loan)
            setStep('form')
          } else {
            setStep('select-borrower')
          }
        } else {
          setStep('form')
        }
      } else if (loan) {
        // Opened from Loan Show page — skip straight to form
        setSelectedLoan(loan)
        setSelectedBorrowerId(loan.borrower_id)
        setStep('form')
        setDate(new Date().toISOString().split('T')[0])
        setNote('')
        setLateFee('')
        if (loan.expected_next_payment) setAmount(String(loan.expected_next_payment.amount))
        else setAmount('')
      } else {
        // Opened from Payments Index / Dashboard — start with borrower selection
        setSelectedBorrowerId(null)
        setSelectedLoan(null)
        setStep('select-borrower')
        setDate(new Date().toISOString().split('T')[0])
        setNote('')
        setLateFee('')
        setAmount('')
      }
    }
  }, [open, loan?.id, payment?.id])

  // Live P/I split calculation
  const liveSplit = useMemo(() => {
    const amt = parseFloat(amount) || 0
    if (!expected || amt <= 0) return null

    const expInterest = expected.interest
    const expPrincipal = expected.principal

    let interest: number, principal: number

    if (amt >= expInterest + expPrincipal) {
      interest = expInterest
      principal = Math.round((amt - expInterest) * 100) / 100
    } else if (amt <= expInterest) {
      interest = amt
      principal = 0
    } else {
      interest = expInterest
      principal = Math.round((amt - expInterest) * 100) / 100
    }

    return { interest, principal }
  }, [amount, expected])

  function findLoanById(borrowers: BorrowerWithLoans[], loanId: number) {
    for (const b of borrowers) {
      const l = b.active_loans.find((loan) => loan.id === loanId)
      if (l) return { borrowerId: b.id, loan: l }
    }
    return null
  }

  function handleSelectBorrower(borrowerId: number) {
    setSelectedBorrowerId(borrowerId)
    setBorrowerSearch('')
    const borrower = borrowersWithLoans?.find((b) => b.id === borrowerId)
    if (!borrower) return

    if (borrower.active_loans.length === 1) {
      // Auto-select the only loan and skip to form
      setSelectedLoan(borrower.active_loans[0])
      setStep('form')
    } else {
      setSelectedLoan(null)
      setStep('select-loan')
    }
  }

  function handleSelectLoan(loan: LoanProps) {
    setSelectedLoan(loan)
    setStep('form')
  }

  function handleChangeLoan() {
    // Go back to borrower selection
    setSelectedBorrowerId(null)
    setSelectedLoan(null)
    setBorrowerSearch('')
    setStep('select-borrower')
  }

  function handleErrorResponse(errors: Record<string, string>) {
    setSubmitting(false)
    setFormErrors({})
    setGeneralError('')

    if (errors && typeof errors === 'object' && Object.keys(errors).length > 0) {
      // Field-level validation errors from Inertia
      const fieldErrors: Record<string, string> = {}
      const nonFieldMessages: string[] = []

      for (const [key, value] of Object.entries(errors)) {
        const message = Array.isArray(value) ? value[0] : String(value)
        if (['amount', 'date', 'note', 'late_fee'].includes(key)) {
          fieldErrors[key] = message
        } else {
          nonFieldMessages.push(message)
        }
      }

      setFormErrors(fieldErrors)
      if (nonFieldMessages.length > 0) {
        setGeneralError(nonFieldMessages.join('. '))
      }
    } else {
      setGeneralError('Something went wrong. Please try again.')
    }
  }

  function handleSuccess() {
    setSubmitting(false)
    setShowSuccess(true)
    setTimeout(() => {
      onClose()
    }, 800)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const loanId = activeLoan?.id ?? loanIdProp
    if (!loanId) return

    setSubmitting(true)
    setFormErrors({})
    setGeneralError('')

    const paymentData = {
      payment: {
        amount: parseFloat(amount),
        date,
        note: note || null,
        late_fee: parseFloat(lateFee) || 0,
        // Include new_loan_id if the loan was changed in edit mode
        ...(isEditMode && activeLoan && originalLoanId && activeLoan.id !== originalLoanId
          ? { new_loan_id: activeLoan.id }
          : {}),
      },
    }

    const requestOptions = {
      onSuccess: handleSuccess,
      onError: handleErrorResponse,
      preserveScroll: true,
    }

    if (isEditMode && payment) {
      // Always PATCH against the original loan's URL
      const patchLoanId = originalLoanId ?? loanId
      router.patch(`/loans/${patchLoanId}/payments/${payment.id}`, paymentData, requestOptions)
    } else {
      router.post(`/loans/${loanId}/payments`, paymentData, requestOptions)
    }
  }

  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </TransitionChild>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
            leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
              <DialogTitle className="text-lg font-bold text-gray-900 mb-4">
                {isEditMode ? 'Edit Payment' : 'Record Payment'}
              </DialogTitle>

              {/* Step 1: Borrower Selection */}
              {step === 'select-borrower' && borrowersWithLoans && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">Select Borrower</label>
                  <div className="relative">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                    </svg>
                    <input
                      ref={searchRef}
                      type="text"
                      value={borrowerSearch}
                      onChange={(e) => setBorrowerSearch(e.target.value)}
                      placeholder="Search borrowers..."
                      className="w-full py-2.5 pl-9 pr-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      autoFocus
                    />
                  </div>

                  <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-200">
                    {filteredBorrowers.length === 0 ? (
                      <p className="px-4 py-6 text-sm text-gray-400 text-center">No borrowers with active loans</p>
                    ) : (
                      filteredBorrowers.map((b) => (
                        <button
                          key={b.id}
                          type="button"
                          onClick={() => handleSelectBorrower(b.id)}
                          className="w-full text-left px-4 py-3 text-sm hover:bg-emerald-50 transition-colors border-b border-gray-50 last:border-b-0 flex items-center justify-between"
                        >
                          <div>
                            <span className="font-medium text-gray-900">{b.name}</span>
                            <span className="ml-2 text-xs text-gray-400">
                              {b.active_loans.length} active loan{b.active_loans.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <svg className="w-4 h-4 text-gray-300" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                          </svg>
                        </button>
                      ))
                    )}
                  </div>

                  <div className="flex justify-end pt-2">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Loan Selection (cards) */}
              {step === 'select-loan' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700">
                      Select Loan for{' '}
                      <span className="text-emerald-600">
                        {borrowersWithLoans?.find((b) => b.id === selectedBorrowerId)?.name}
                      </span>
                    </label>
                    <button
                      type="button"
                      onClick={() => { setStep('select-borrower'); setBorrowerSearch('') }}
                      className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                    >
                      Change borrower
                    </button>
                  </div>

                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {selectedBorrowerLoans.map((l) => (
                      <LoanCard key={l.id} loan={l} onSelect={() => handleSelectLoan(l)} />
                    ))}
                  </div>

                  <div className="flex justify-end pt-2">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Payment Form */}
              {step === 'form' && (
                <>
                  {/* Selected loan context bar */}
                  {activeLoan && (
                    <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 mb-4">
                      <span className="text-sm text-gray-700 flex items-center gap-2">
                        <span className="font-medium">{activeLoan.borrower_name}</span>
                        <LoanBadge loanNumber={activeLoan.loan_number} />
                        {activeLoan.purpose && (
                          <>
                            <span className="text-gray-400">&middot;</span>
                            <span className="text-gray-500">{activeLoan.purpose}</span>
                          </>
                        )}
                      </span>
                      {/* Show "Change loan" if opened from context that allows switching */}
                      {borrowersWithLoans && (
                        <button
                          type="button"
                          onClick={handleChangeLoan}
                          className="text-xs text-emerald-600 hover:text-emerald-700 font-medium whitespace-nowrap ml-2"
                        >
                          Change loan
                        </button>
                      )}
                    </div>
                  )}

                  {/* Edit mode banner */}
                  {isEditMode && payment && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
                      <p className="text-xs text-amber-700">
                        Editing payment #{payment.id} recorded on {payment.date} — changes will recalculate splits for subsequent payments
                      </p>
                    </div>
                  )}

                  {/* Success banner */}
                  {showSuccess && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-4 flex items-center gap-2">
                      <svg className="w-5 h-5 text-emerald-600 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                      </svg>
                      <p className="text-sm font-medium text-emerald-700">
                        {isEditMode ? 'Payment updated successfully.' : 'Payment recorded successfully.'}
                      </p>
                    </div>
                  )}

                  {/* General error alert */}
                  {generalError && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 flex items-start gap-2">
                      <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
                      </svg>
                      <p className="text-sm text-red-700">{generalError}</p>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Expected payment info card */}
                    {expected && (
                      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                          Payment #{expected.payment_number} — Due {expected.due_date}
                        </p>
                        <div className="grid grid-cols-3 gap-3 text-sm">
                          <div>
                            <span className="text-gray-400 text-xs">Expected</span>
                            <p className="font-semibold text-gray-900 font-mono">
                              ${expected.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-400 text-xs">Principal</span>
                            <p className="font-semibold text-emerald-600 font-mono">
                              ${expected.principal.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-400 text-xs">Interest</span>
                            <p className="font-semibold text-indigo-500 font-mono">
                              ${expected.interest.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Amount */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Amount</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                        <input
                          type="number"
                          step="0.01"
                          value={amount}
                          onChange={(e) => { setAmount(e.target.value); setFormErrors((prev) => { const { amount: _, ...rest } = prev; return rest }) }}
                          className={`w-full py-2.5 pl-7 pr-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 ${formErrors.amount ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                          required
                          autoFocus={step === 'form'}
                        />
                      </div>
                      {formErrors.amount && <p className="mt-1 text-xs text-red-500">{formErrors.amount}</p>}
                    </div>

                    {/* Live P/I split */}
                    {liveSplit && (
                      <div className="flex gap-4 text-sm">
                        <div className="flex-1 bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                          <span className="text-emerald-600 text-xs font-medium">To Principal</span>
                          <p className="font-bold text-emerald-700 font-mono mt-0.5">
                            ${liveSplit.principal.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </p>
                        </div>
                        <div className="flex-1 bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-center">
                          <span className="text-indigo-600 text-xs font-medium">To Interest</span>
                          <p className="font-bold text-indigo-700 font-mono mt-0.5">
                            ${liveSplit.interest.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Date */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Date</label>
                      <input
                        type="date"
                        value={date}
                        onChange={(e) => { setDate(e.target.value); setFormErrors((prev) => { const { date: _, ...rest } = prev; return rest }) }}
                        className={`w-full py-2.5 px-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 ${formErrors.date ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                        required
                      />
                      {formErrors.date && <p className="mt-1 text-xs text-red-500">{formErrors.date}</p>}
                    </div>

                    {/* Late Fee (optional) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Late Fee (optional)</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                        <input
                          type="number"
                          step="0.01"
                          value={lateFee}
                          onChange={(e) => { setLateFee(e.target.value); setFormErrors((prev) => { const { late_fee: _, ...rest } = prev; return rest }) }}
                          placeholder="0.00"
                          className={`w-full py-2.5 pl-7 pr-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 ${formErrors.late_fee ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                        />
                      </div>
                      {formErrors.late_fee && <p className="mt-1 text-xs text-red-500">{formErrors.late_fee}</p>}
                    </div>

                    {/* Note */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Note (optional)</label>
                      <input
                        type="text"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="e.g. Check #1234, Venmo payment"
                        className="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 justify-end pt-2">
                      <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={submitting || showSuccess || !amount || !activeLoan}
                        className="px-6 py-2.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                      >
                        {submitting && (
                          <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                        )}
                        {submitting
                          ? (isEditMode ? 'Updating...' : 'Recording...')
                          : (isEditMode ? 'Update Payment' : 'Record Payment')
                        }
                      </button>
                    </div>
                  </form>
                </>
              )}
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  )
}

/* ── Loan Card for Step 2 ── */

function LoanCard({ loan, onSelect }: { loan: LoanProps; onSelect: () => void }) {
  const LOAN_TYPE_LABELS: Record<string, string> = {
    standard: 'Standard',
    interest_only: 'Interest Only',
    balloon: 'Balloon',
  }

  return (
    <button
      type="button"
      onClick={onSelect}
      className="w-full text-left border border-gray-200 rounded-xl p-4 hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors group"
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <LoanBadge loanNumber={loan.loan_number} />
          <h4 className="text-sm font-semibold text-gray-900 group-hover:text-emerald-700">
            {loan.purpose || ''}
          </h4>
        </div>
        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
          {LOAN_TYPE_LABELS[loan.loan_type] || loan.loan_type}
        </span>
      </div>
      <p className="text-xs font-mono text-gray-600 mb-2">
        ${loan.principal.toLocaleString('en-US', { minimumFractionDigits: 2 })}
      </p>
      <div className="grid grid-cols-3 gap-x-4 gap-y-1.5 text-xs">
        <div>
          <span className="text-gray-400">Principal</span>
          <p className="font-semibold text-gray-700 font-mono">
            ${loan.principal.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </p>
        </div>
        <div>
          <span className="text-gray-400">Rate</span>
          <p className="font-semibold text-gray-700">{loan.annual_rate}%</p>
        </div>
        <div>
          <span className="text-gray-400">Term</span>
          <p className="font-semibold text-gray-700">{loan.term_months} mo</p>
        </div>
        <div>
          <span className="text-gray-400">Monthly</span>
          <p className="font-semibold text-gray-700 font-mono">
            ${loan.monthly_payment.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </p>
        </div>
        <div>
          <span className="text-gray-400">Remaining</span>
          <p className="font-semibold text-emerald-600 font-mono">
            ${loan.remaining_balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </p>
        </div>
        <div>
          <span className="text-gray-400">Next Due</span>
          <p className="font-semibold text-gray-700">
            {loan.next_payment_due || '—'}
          </p>
        </div>
      </div>
    </button>
  )
}
